import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Device } from "@twilio/voice-sdk";
import { PhoneCall, PhoneOff, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Recovery is a separate, explicitly-assigned bucket -- old/lost/not-taken/lapsed
// opportunities from the existing book-recovery workbook pipeline. It is never
// automatically mixed with Auto Dialer (cold leads) or Client Follow-Up
// (current-client servicing); GET /api/book-recovery/cases already scopes a
// bpo_closer to only cases explicitly assigned to them.
export default function RecoveryDialer() {
  const [cases, setCases] = useState(null);
  const [loading, setLoading] = useState(false);
  const [callingId, setCallingId] = useState(null);
  const deviceRef = useRef(null);
  const connRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/book-recovery/cases").then(({ data }) => setCases(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load Recovery cases"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cleanupDevice = () => {
    try { connRef.current?.disconnect(); } catch (e) {}
    try { deviceRef.current?.destroy(); } catch (e) {}
    connRef.current = null;
    deviceRef.current = null;
    setCallingId(null);
  };
  useEffect(() => () => cleanupDevice(), []);

  const call = async (c) => {
    const phone = c.phone || c.phone_digits;
    if (!phone) { toast.error("No phone number on this case"); return; }
    try {
      const { data: callData } = await api.post("/voice/calls", { to: phone });
      const { data: t } = await api.post("/voice/token");
      const device = new Device(t.token, { logLevel: 1 });
      deviceRef.current = device;
      device.on("error", () => cleanupDevice());
      const conn = await device.connect({ params: { To: phone, CallLogId: callData.call_log_id } });
      connRef.current = conn;
      setCallingId(c.id);
      ["disconnect", "cancel", "reject", "error"].forEach((ev) => conn.on(ev, () => cleanupDevice()));
    } catch (err) {
      cleanupDevice();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Call could not start");
    }
  };

  const rows = cases || [];
  const readyToCall = rows.filter((c) => (c.recovery_status || "Open") === "Open" && (c.phone || c.phone_digits));

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-gold/40 bg-gold/5 p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="recovery-scope-note">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-gold-deep" />
        Recovery only shows old/lost/not-taken/lapsed cases explicitly assigned to you. It never pulls from Auto Dialer or Client Follow-Up.
      </div>

      <div className="rounded-lg border border-border bg-card p-5" data-testid="recovery-dialer">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="overline">Recovery Cases</div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="recovery-refresh">
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm mb-4">
          <Badge variant="secondary" data-testid="recovery-ready-count">{readyToCall.length} ready to call</Badge>
          <Badge variant="outline" data-testid="recovery-total-count">{rows.length} assigned to you</Badge>
        </div>

        {loading && !cases ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No Recovery cases are explicitly assigned to you right now.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`recovery-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{c.client_name || "Unknown client"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{c.phone || c.phone_digits || "no phone on file"}</div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                    {c.carrier && <span>Carrier: {c.carrier}</span>}
                    {c.recovery_bucket && <span>{c.recovery_bucket}</span>}
                    <Badge variant={c.recovery_status === "Resolved" ? "outline" : "secondary"} className="h-4 px-1.5 text-[10px]">
                      {c.recovery_status || "Open"}
                    </Badge>
                  </div>
                </div>
                {callingId === c.id ? (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Calling
                    </span>
                    <Button size="sm" variant="destructive" onClick={cleanupDevice} data-testid={`recovery-hangup-${c.id}`}>
                      <PhoneOff className="mr-1.5 h-4 w-4" /> Hang Up
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => call(c)} disabled={!!callingId || !(c.phone || c.phone_digits)} data-testid={`recovery-call-${c.id}`}>
                    <PhoneCall className="mr-1.5 h-4 w-4" /> Call
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
